/**
 * Capstone submission page — Sprint 4 / FR-G.
 *
 * Behavior:
 *
 * - On load, GET the user's existing capstones for this course.
 * - If a draft exists, open it for editing (PUT). M4 OCC: the form
 *   tracks ``updated_at`` from the last server response.
 * - If no draft exists, the form creates a new one via POST.
 * - "Save draft" → POST/PUT with status='draft'; no exam gate.
 * - "Submit for grading" → POST with status='submitted'; surfaces
 *   FR-G-1 exam-pass gate as a clear inline error.
 * - Word count is server-validated on submit (1500..3000); client
 *   shows a live count + over/under warning so the candidate isn't
 *   surprised.
 */
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { LessonBody } from "@/components/LessonBody";
import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  CapstoneExamGateError,
  editCapstoneDraft,
  listCapstones,
  writeCapstone,
  type CapstoneSubmission,
} from "@/lib/exam-api";

// The canonical capstone case prompt (the 7-question "Maya R." case) lives
// in content/ and is bundled by LessonBody's MDX glob; render it here so the
// editor shows the real assignment instead of a generic one-liner.
const CAPSTONE_CASE_PROMPT_PATH = "content/capstone/case-prompt.mdx";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; existing: CapstoneSubmission[] }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function countWords(s: string): number {
  return (s.match(/\S+/g) ?? []).length;
}

export interface CapstonePageProps {
  onUnauthenticated?: (returnTo: string) => void;
}

export function CapstonePage({
  onUnauthenticated = redirectToLogin,
}: CapstonePageProps = {}): React.JSX.Element {
  const { courseSlug = "cait-foundations" } = useParams<{ courseSlug: string }>();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [body, setBody] = useState<string>("");
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftUpdatedAt, setDraftUpdatedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [statusBanner, setStatusBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initial load: fetch existing submissions, prefill draft if present.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const existing = await listCapstones(courseSlug);
        if (cancelled) return;
        setLoad({ kind: "ready", existing });
        const draft = existing.find((c) => c.status === "draft");
        if (draft) {
          setBody(draft.body);
          setDraftId(draft.id);
          setDraftUpdatedAt(draft.updated_at);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated(`/capstone/${courseSlug}`);
          return;
        }
        const msg =
          err instanceof Error ? err.message : "Could not load capstones.";
        setLoad({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug, onUnauthenticated]);

  const wordCount = useMemo(() => countWords(body), [body]);
  const min = load.kind === "ready" && load.existing[0]?.word_count_min
    ? load.existing[0].word_count_min
    : 1500;
  const max = load.kind === "ready" && load.existing[0]?.word_count_max
    ? load.existing[0].word_count_max
    : 3000;

  const onSaveDraft = useCallback(async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      let result: CapstoneSubmission;
      if (draftId && draftUpdatedAt) {
        result = await editCapstoneDraft(draftId, body, draftUpdatedAt);
      } else {
        result = await writeCapstone(courseSlug, body, "draft");
      }
      setDraftId(result.id);
      setDraftUpdatedAt(result.updated_at);
      setStatusBanner(`Draft saved at ${new Date(result.updated_at).toLocaleTimeString()}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save draft.");
    } finally {
      setSaving(false);
    }
  }, [body, courseSlug, draftId, draftUpdatedAt]);

  const onSubmit = useCallback(async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      // Submit the (possibly edited) body. The backend promotes the open
      // draft to 'submitted' in place rather than inserting a new row, so a
      // Save-draft + Submit pair is ONE logical attempt — drafts no longer
      // silently consume one of the two graded-attempt slots.
      const result = await writeCapstone(courseSlug, body, "submitted");
      setStatusBanner(
        `Submitted (attempt ${result.attempt_number}) for AI review — results in minutes. Watch your inbox.`,
      );
      // The draft is now submitted; drop our draft handle so a later revision
      // starts a fresh attempt (a new draft row) rather than PUT-ing a
      // now-immutable submitted row.
      setDraftId(null);
      setDraftUpdatedAt(null);
      // Re-fetch the list to reflect the new submission.
      const refreshed = await listCapstones(courseSlug);
      setLoad({ kind: "ready", existing: refreshed });
    } catch (err) {
      if (err instanceof CapstoneExamGateError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Could not submit.");
      }
    } finally {
      setSubmitting(false);
    }
  }, [body, courseSlug]);

  if (load.kind === "loading") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6" aria-busy="true">
        <p className="text-sm text-muted-foreground" role="status">
          Loading your capstone…
        </p>
      </section>
    );
  }
  if (load.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground">Capstone</h1>
        <p className="mt-4 text-sm text-red-700" role="alert">
          {load.message}
        </p>
      </section>
    );
  }

  const submittedAttempts = load.existing.filter(
    (c) => c.status === "submitted" || c.status === "graded",
  );
  const attemptsRemaining = 2 - submittedAttempts.length;

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Foundations Certificate · Capstone
        </p>
        <h1 className="mt-1 text-3xl font-bold text-foreground">
          Capstone essay
        </h1>
        <p className="mt-3 text-sm text-foreground">
          Write a {min}–{max}-word case write-up for the published capstone
          case below, addressing every prompt question in §4. It's graded
          against the five-criterion rubric. You can save drafts freely;
          submission is gated on passing the final exam, and you have a
          maximum of 2 graded attempts.
        </p>
      </header>

      <details
        className="mt-6 rounded-lg border border-border bg-white p-4 sm:p-6"
        open
      >
        <summary className="cursor-pointer text-sm font-semibold text-foreground">
          Capstone case &amp; prompt — read this first
        </summary>
        <div className="mt-4 border-t border-border pt-2">
          <LessonBody articleMdxPath={CAPSTONE_CASE_PROMPT_PATH} />
        </div>
      </details>

      {submittedAttempts.length > 0 ? (
        <article className="mt-8 rounded-lg border border-border bg-background p-4 text-sm">
          <p className="font-semibold text-foreground">
            {submittedAttempts.length} submitted attempt
            {submittedAttempts.length === 1 ? "" : "s"} ·{" "}
            {attemptsRemaining > 0
              ? `${attemptsRemaining} remaining`
              : "no more attempts"}
          </p>
          <ul className="mt-2 space-y-1 text-foreground">
            {submittedAttempts.map((c) => (
              <li key={c.id}>
                Attempt {c.attempt_number} — submitted{" "}
                {c.submitted_at
                  ? new Date(c.submitted_at).toLocaleDateString()
                  : "(date unknown)"}{" "}
                · {c.word_count} words · status: {c.status}
              </li>
            ))}
          </ul>
        </article>
      ) : null}

      <article className="mt-8 rounded-lg border border-border bg-white p-6">
        <label className="block">
          <span className="text-sm font-semibold text-foreground">
            Your essay
          </span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={20}
            disabled={saving || submitting || attemptsRemaining === 0}
            placeholder="Begin your reflection here…"
            className="mt-2 w-full rounded-md border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </label>
        <p
          className={`mt-2 text-xs ${
            wordCount < min
              ? "text-amber-700"
              : wordCount > max
                ? "text-red-700"
                : "text-emerald-700"
          }`}
          data-testid="word-count"
        >
          {wordCount} words ({min}–{max} required for submission)
        </p>
        {statusBanner !== null ? (
          <p
            className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
            role="status"
          >
            {statusBanner}
          </p>
        ) : null}
        {error !== null ? (
          <p
            className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              void onSaveDraft();
            }}
            disabled={saving || submitting || attemptsRemaining === 0}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            onClick={() => {
              void onSubmit();
            }}
            disabled={
              saving ||
              submitting ||
              attemptsRemaining === 0 ||
              wordCount < min ||
              wordCount > max
            }
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {submitting ? "Submitting…" : "Submit for grading"}
          </button>
        </div>
      </article>
    </section>
  );
}
